import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from '../lib/api/client'

interface ServerConfig {
  serverMode: 'production' | 'development' | 'test'
  isTest: boolean
  paymentProvider: string
}

interface ServerConfigContextType {
  config: ServerConfig
  loading: boolean
}

const defaultConfig: ServerConfig = {
  serverMode: 'production',
  isTest: false,
  paymentProvider: 'stripe'
}

const ServerConfigContext = createContext<ServerConfigContextType>({
  config: defaultConfig,
  loading: true
})

export function useServerConfig() {
  return useContext(ServerConfigContext)
}

interface ServerConfigProviderProps {
  children: ReactNode
}

export function ServerConfigProvider({ children }: ServerConfigProviderProps) {
  const [config, setConfig] = useState<ServerConfig>(defaultConfig)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await apiClient.getServerConfig()
        setConfig({
          serverMode: response.server_mode as ServerConfig['serverMode'],
          isTest: response.is_test,
          paymentProvider: response.payment_provider
        })
      } catch (error) {
        console.error('Failed to fetch server config:', error)
        // Keep default config on error
      } finally {
        setLoading(false)
      }
    }

    fetchConfig()
  }, [])

  return (
    <ServerConfigContext.Provider value={{ config, loading }}>
      {children}
    </ServerConfigContext.Provider>
  )
}

